/* eslint-disable jsx-a11y/anchor-has-content */
import { useState } from 'react';
import styled from '@emotion/styled';
import { scroller } from 'react-scroll';

import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Loader } from '@/components/common/Loader';
import { HeadingLevel } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { FormError, HubspotForm } from '@/components/forms/HubspotForm';
import { useFormData } from '@/components/forms/useFormData';
import { Section } from '@/components/layout/Section';
import { bodySmall } from '@/styles/typography';

import { Thankyou } from './Thankyou';

import type { SanityHubspotFormBlockType } from '@/types/sanity';
import type {
  BlockWithAudience,
  PageData,
  ProgrammeType,
} from '@/types/shared';

export interface HubspotFormBlockProps
  extends Omit<SanityHubspotFormBlockType, 'thankYouCTAHrefInternal'>,
    BlockWithAudience {
  thankYouCTAHrefInternal?: PageData | ProgrammeType;
}

export const HubspotFormBlock = ({
  formBackgroundColour,
  blockSpacing,
  paddingAdjustment,
  formHeading,
  description,
  hubspotFormId,
  breakpoints,
  textUnderForm,
  thankYouMsgHeading,
  thankYouMsgBody,
  thankYouCTALabel,
  thankYouCTAHrefExternal,
  thankYouCTAHrefInternal,
  downloadFile,
  pageAudience,
  isAudienceSwitcherEnabled,
}: HubspotFormBlockProps) => {
  const backgroundColourToken = formBackgroundColour?.token;

  const [submitted, setSubmitted] = useState(false);

  const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
  const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET;

  const response = useFormData(hubspotFormId);
  const { data, error, isValidating } = response || {};

  if (data?.status === 'error' || error) return <FormError />;
  if (isValidating) return <Loader />;

  const formIsSubmitted = () => {
    scroller.scrollTo('hubspot-form-container', {
      duration: 1000,
      delay: 100,
      offset: -110,
      smooth: true,
    });
    setSubmitted(true);
  };

  const getDownloadLink = (downloadableFile) => {
    const downloadData = downloadableFile.asset._ref.split('-');
    const url = `https://cdn.sanity.io/files/${projectId}/${dataset}/${downloadData[1]}.${downloadData[2]}`;
    return url;
  };

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="hubspot-form-block"
    >
      <HeadingLevel>
        <FormPane id="hubspot-form-container">
          <FormContainer>
            {submitted ? (
              <Thankyou
                thankYouMsgHeading={thankYouMsgHeading}
                thankYouMsgBody={thankYouMsgBody}
                thankYouCTALabel={thankYouCTALabel}
                thankYouCTAHrefExternal={thankYouCTAHrefExternal}
                thankYouCTAHrefInternal={thankYouCTAHrefInternal}
                downloadFile={downloadFile ? getDownloadLink(downloadFile) : ''}
                pageAudience={pageAudience}
                isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
              />
            ) : (
              <>
                {!!formHeading && (
                  <StyledCaptionHeadingDescription
                    heading={formHeading}
                    description={description}
                    desktopAlignment="start"
                    mobileAlignment="center"
                    pageAudience={pageAudience}
                    isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
                  />
                )}
                {data ? (
                  <FormWrapper backgroundColour={backgroundColourToken}>
                    <HubspotForm
                      formData={data}
                      onSubmit={formIsSubmitted}
                      breakpoints={breakpoints}
                      formType="hubspotBlock"
                    />
                  </FormWrapper>
                ) : null}
                {textUnderForm && (
                  <TextUnderForm>
                    <PortableText
                      value={textUnderForm}
                      audienceConfig={{
                        pageAudience,
                        isAudienceSwitcherEnabled,
                      }}
                    />
                  </TextUnderForm>
                )}
              </>
            )}
          </FormContainer>
        </FormPane>
      </HeadingLevel>
    </Section>
  );
};

const TextUnderForm = styled.div`
  margin: var(--spacing-xxx-small) 0;
  padding: var(--spacing-small);
  ${bodySmall}
  a {
    color: var(--text-action);
  }
`;

const FormPane = styled.div`
  position: relative;

  ${screen.md} {
    height: 100%;
  }
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  padding-bottom: var(--spacing-500);
`;

const FormContainer = styled.div`
  ${screen.md} {
    margin: 0 auto;

    max-width: 1060px;
  }

  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-l);
  position: relative;
  z-index: 2;
`;

const FormWrapper = styled.div<{ backgroundColour?: string }>`
  position: relative;
  background-color: ${({ backgroundColour }) =>
    backgroundColour === '--background-subtle'
      ? 'var(--background-cool-base)'
      : 'var(--background-subtle)'};
  padding: var(--spacing-300);
  width: 100%;
  border-radius: var(--radius-m);
  min-height: 300px;

  border: 1px solid var(--border-cool-subtle);

  ${screen.md} {
    padding: var(--spacing-xx-large);
  }
`;
